import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { Reaction } from "@src/appV2/redesign/Workplace/Review/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { workplaceCommentSchema } from "./useWorkplaceComments";

const batchReactionItemSchema = z.object({
  commentId: z.string(),
  reaction: z.nativeEnum(Reaction),
});

const postBatchReactionsRequestSchema = z.object({
  reactions: z.array(batchReactionItemSchema),
});

const postBatchReactionsResponseSchema = z.object({
  data: z.array(workplaceCommentSchema),
});

export type PostBatchReactionsRequest = z.infer<typeof postBatchReactionsRequestSchema>;
export type PostBatchReactionsResponse = z.infer<typeof postBatchReactionsResponseSchema>;

interface UsePostBatchReactionsParams {
  workplaceId: string;
}

function getBatchReactionsUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/${workplaceId}/comments/reactions/batch`;
}

export function usePostBatchReactions(
  params: UsePostBatchReactionsParams,
  options: UseMutationOptions<
    PostBatchReactionsResponse,
    AxiosError,
    PostBatchReactionsRequest
  > = {}
): UseMutationResult<PostBatchReactionsResponse, AxiosError, PostBatchReactionsRequest> {
  const { workplaceId } = params;
  return useMutation({
    mutationFn: async (data: PostBatchReactionsRequest) => {
      const response = await post({
        url: getBatchReactionsUrl(workplaceId),
        data,
        requestSchema: postBatchReactionsRequestSchema,
        responseSchema: postBatchReactionsResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.POST_COMMENT_REACTION,
      userErrorMessage: "Failed to post reactions",
    },
    ...options,
  });
}
